import { useRef, useEffect, useState, useImperativeHandle, forwardRef, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, Rewind, FastForward } from "lucide-react";
import { fmtTime } from "@/lib/format";
import { drawFrame, drawCaptionFrame, hasAnyCaptionObjects } from "@/lib/animator";
import { ensureScriptFonts, ensureTextFonts, onFontsReady, collectCaptionText } from "@/lib/fonts";
import { computeImageEffect } from "@/lib/imageEffects";
import { drawElement } from "@/lib/elements";
import { resolveActiveVideoClip, resolveActiveImages, resolveActiveElements, sourceTimeFor, clipSourceUrl, absUrl, cssFilter, prevVideoClip, audioClips, videoClips, clipCrop, cropRect, clipOpacity } from "@/lib/timeline";
import { CropOverlay } from "@/components/CropOverlay";
import { TrackOverlay } from "@/components/TrackOverlay";
import { cameraTf, applyCamera } from "@/lib/motion";
import { traceShape, isShaped, backgroundObject } from "@/lib/shapes";

const SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

// Timeline-driven preview compositor. ONE timeline clock is the authority. Each frame we
// resolve the active video clip (mapping timeline time → source time), draw it to the canvas,
// composite active image overlays, then captions. Hidden <video> elements are reused as decode
// sources. Export (FFmpeg) uses the same timeline interpretation (see lib/timeline.js).
const VideoPlayer = forwardRef(function VideoPlayer(
  { videoUrl, videoMeta, segments, config, captionLayers, selectedObjId, onSelectCaption, onMoveCaption, previewMode = "combined", onTime, onPlayStateChange, mediaTracks = [], duration: timelineDur = 0, canvasRatio = null, fitMode = "cover", letterbox = "black", cropEdit = null, onCropChange = null, onCropDone = null, trackDraw = null }, ref
) {
  const canvasRef = useRef(null);
  const cropEditRef = useRef(null);
  cropEditRef.current = cropEdit;
  const cropGeomRef = useRef(null);
  const wrapRef = useRef(null);
  const outerRef = useRef(null);
  const hiddenRef = useRef(null);
  const rafRef = useRef(null);
  const hitboxesRef = useRef([]);
  const dragRef = useRef(null);
  const videosRef = useRef(new Map()); // url -> HTMLVideoElement (decode source pool)
  const audiosRef = useRef(new Map()); // url -> HTMLAudioElement (music/voiceover pool)
  const imagesRef = useRef(new Map()); // url -> HTMLImageElement
  const activeElRef = useRef(null);
  const curRef = useRef(0);           // authoritative timeline time
  const clockRef = useRef({ wall: 0, t: 0 });
  const playingRef = useRef(false);
  const speedRef = useRef(1);
  const volRef = useRef(1);
  const mutedRef = useRef(false);

  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);

  const duration = timelineDur || videoMeta?.duration || 0;
  // Preview canvas follows the chosen OUTPUT aspect (export), not the source video, so the
  // preview always mirrors what the export will produce.
  const ratio = canvasRatio || (videoMeta?.width && videoMeta?.height ? videoMeta.width / videoMeta.height : 9 / 16);
  const fitRef = useRef({ fitMode, letterbox });
  fitRef.current = { fitMode, letterbox };
  const interactive = previewMode !== "video" && !!onMoveCaption && hasAnyCaptionObjects(captionLayers);

  // Latest values available to the imperative render loop (avoids stale closures).
  const stateRef = useRef({});
  stateRef.current = { mediaTracks, videoUrl, previewMode, segments, config, captionLayers, selectedObjId, duration };

  const getVideo = useCallback((url) => {
    if (!url) return null;
    const map = videosRef.current;
    if (map.has(url)) {
      const cached = map.get(url);
      if (hiddenRef.current && !hiddenRef.current.contains(cached)) hiddenRef.current.appendChild(cached);
      if (!cached.getAttribute("src")) cached.src = url;
      return cached;
    }
    const el = document.createElement("video");
    el.src = url; el.preload = "auto"; el.playsInline = true;
    el.muted = false; el.style.display = "none";
    el.addEventListener("loadeddata", () => {
      if (!playingRef.current && activeElRef.current === el) {
        if (!el.__primed) { try { el.currentTime = (el.currentTime || 0) + 0.001; el.__primed = true; } catch { /* noop */ } }
        drawRef.current(curRef.current);
      }
    });
    el.addEventListener("seeked", () => { if (!playingRef.current && activeElRef.current === el) drawRef.current(curRef.current); });
    if (hiddenRef.current) hiddenRef.current.appendChild(el);
    map.set(url, el);
    return el;
  }, []);

  const getImage = useCallback((url) => {
    if (!url) return null;
    const map = imagesRef.current;
    if (map.has(url)) return map.get(url);
    const img = new Image();
    img.onload = () => { if (!playingRef.current) drawRef.current(curRef.current); };
    img.src = url;
    map.set(url, img);
    return img;
  }, []);

  const getAudio = useCallback((url) => {
    if (!url) return null;
    const map = audiosRef.current;
    if (map.has(url)) return map.get(url);
    const el = document.createElement("audio");
    el.src = url; el.preload = "auto"; el.style.display = "none";
    if (hiddenRef.current) hiddenRef.current.appendChild(el);
    map.set(url, el);
    return el;
  }, []);

  // Effective video clips: if the project has no explicit video clip, treat the whole timeline
  // as one 1:1 clip of the source video (legacy / caption-only projects).
  const effectiveActiveClip = useCallback((t) => {
    const { mediaTracks: mt, videoUrl: vu, duration: d } = stateRef.current;
    const active = resolveActiveVideoClip(mt, t);
    if (active) return active;
    const anyVideoTrack = (mt || []).some((tr) => tr.type === "video" && (tr.objects || []).length);
    if (!anyVideoTrack && vu && t < d) {
      return { tr: { type: "video" }, o: { start: 0, end: d, source_start: 0, source_end: d } };
    }
    return null;
  }, []);

  const drawCover = (ctx, el, cw, ch, crop = null, noBars = false) => {
    if (!el.videoWidth || !el.videoHeight) return false;
    const [sx, sy, vw, vh] = cropRect(crop, el.videoWidth, el.videoHeight);
    const { fitMode: fm, letterbox: lb } = fitRef.current;
    if (fm === "contain") {
      // letterbox fill first (black or a blurred cover copy), then the full frame centered
      if (lb === "blur") {
        const bs = Math.max(cw / vw, ch / vh), bw = vw * bs, bh = vh * bs;
        ctx.save();
        try { ctx.filter = "blur(24px)"; } catch { /* noop */ }
        ctx.drawImage(el, sx, sy, vw, vh, (cw - bw) / 2, (ch - bh) / 2, bw, bh);
        ctx.restore();
      } else if (!noBars) {
        ctx.fillStyle = "#000"; ctx.fillRect(0, 0, cw, ch);
      }
      const scale = Math.min(cw / vw, ch / vh);
      const dw = vw * scale, dh = vh * scale;
      ctx.drawImage(el, sx, sy, vw, vh, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
      return true;
    }
    const scale = Math.max(cw / vw, ch / vh);
    const dw = vw * scale, dh = vh * scale;
    ctx.drawImage(el, sx, sy, vw, vh, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    return true;
  };

  const drawBackground = (ctx, cw, ch) => {
    const bg = backgroundObject(stateRef.current.mediaTracks);
    if (!bg) return false;
    const op = Math.max(0, Math.min(1, bg.opacity != null ? bg.opacity : 1));
    ctx.save();
    ctx.globalAlpha = op;
    const bt = bg.bgType || "solid";
    if (bt === "gradient") {
      const ang = ((bg.angle != null ? bg.angle : 90) * Math.PI) / 180;
      const dx = Math.cos(ang), dy = Math.sin(ang);
      const x0 = cw / 2 - (dx * cw) / 2, y0 = ch / 2 - (dy * ch) / 2;
      const x1 = cw / 2 + (dx * cw) / 2, y1 = ch / 2 + (dy * ch) / 2;
      const g = ctx.createLinearGradient(x0, y0, x1, y1);
      g.addColorStop(0, bg.from || "#111827"); g.addColorStop(1, bg.to || "#000000");
      ctx.fillStyle = g; ctx.fillRect(0, 0, cw, ch);
    } else if (bt === "image" && (bg.asset_url || bg.assetUrl)) {
      const img = getImage(absUrl(bg.asset_url || bg.assetUrl));
      if (img && img.naturalWidth) {
        const fit = bg.fit || "cover";
        const r = fit === "contain" ? Math.min(cw / img.naturalWidth, ch / img.naturalHeight) : Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
        const dw = img.naturalWidth * r, dh = img.naturalHeight * r;
        if (fit === "contain") { ctx.fillStyle = "#000"; ctx.fillRect(0, 0, cw, ch); }
        ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
      } else { ctx.fillStyle = "#000"; ctx.fillRect(0, 0, cw, ch); }
    } else if (bt === "video" && (bg.asset_url || bg.assetUrl)) {
      const el = getVideo(absUrl(bg.asset_url || bg.assetUrl));
      if (el && el.readyState >= 2) drawCover(ctx, el, cw, ch);
      else { ctx.fillStyle = "#000"; ctx.fillRect(0, 0, cw, ch); }
    } else {
      ctx.fillStyle = bg.color || "#000000"; ctx.fillRect(0, 0, cw, ch);
    }
    ctx.restore();
    return true;
  };

  // Draw the active video into its shape box with an independent internal transform.
  const drawShapedVideo = (ctx, el, cw, ch, o, alpha) => {
    const vw = el.videoWidth, vh = el.videoHeight;
    if (!vw || !vh) return;
    const sw = Math.max(0.02, Math.min(1, o.shapeW != null ? o.shapeW : 1));
    const sh = Math.max(0.02, Math.min(1, o.shapeH != null ? o.shapeH : 1));
    const bw = cw * sw, bh = ch * sh;
    const cx = (o.shapeX != null ? o.shapeX : 0.5) * cw;
    const cy = (o.shapeY != null ? o.shapeY : 0.5) * ch;
    const ms = Math.max(0.1, o.mScale != null ? o.mScale : 1);
    const mx = o.mX || 0, my = o.mY || 0;
    const mfit = o.mFit || "cover";
    const rot = ((o.rotation || 0) * Math.PI) / 180;
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, alpha == null ? 1 : alpha));
    ctx.filter = cssFilter(o.filters);
    ctx.translate(cx, cy);
    if (rot) ctx.rotate(rot);
    traceShape(ctx, bw, bh, o.shape || "rect", o.cornerRadius || 0.12);
    ctx.clip();
    const base = mfit === "contain" ? Math.min(bw / vw, bh / vh) : Math.max(bw / vw, bh / vh);
    const r = base * ms;
    const dw = vw * r, dh = vh * r;
    ctx.drawImage(el, -dw / 2 - mx * bw, -dh / 2 - my * bh, dw, dh);
    ctx.restore();
    ctx.filter = "none";
  };

  const draw = useCallback((t) => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const cw = cv.width, ch = cv.height;
    const { previewMode: pm, videoUrl: vu, segments: segs, config: cfg, captionLayers: caps, selectedObjId: selId } = stateRef.current;
    ctx.clearRect(0, 0, cw, ch);

    // 1) Video frame (skip in captions-only "overlay" mode → transparent checker shows through)
    if (pm !== "overlay") {
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, cw, ch);
      // 1a) Background Canvas (solid / gradient / image / video) behind the video.
      drawBackground(ctx, cw, ch);
      const clip = effectiveActiveClip(t);
      if (clip) {
        const url = clipSourceUrl(clip.o, vu);
        const el = getVideo(url);
        activeElRef.current = el;
        // Transition-in for this clip (fade / crossfade / slide / zoom).
        const tr = clip.o.transition;
        let alpha = 1, tx = 0, sc = 1;
        if (tr && tr.type && tr.type !== "none" && (tr.duration || 0) > 0 && t < (clip.o.start || 0) + tr.duration) {
          const p = Math.min(1, Math.max(0, (t - (clip.o.start || 0)) / tr.duration));
          if (tr.type === "fade") alpha = p;
          else if (tr.type === "slide") tx = (1 - p) * cw * (tr.direction === "left" ? -1 : 1);
          else if (tr.type === "zoom") { sc = 1.2 - 0.2 * p; alpha = p < 0.2 ? p / 0.2 : 1; }
          else if (tr.type === "crossfade") {
            const prev = prevVideoClip(stateRef.current.mediaTracks, clip.o);
            if (prev) {
              const pel = getVideo(clipSourceUrl(prev.o, vu));
              if (pel) {
                const pst = sourceTimeFor(prev.o, Math.max(0, (prev.o.end || 0) - 0.06));
                if (Math.abs(pel.currentTime - pst) > 0.12 && pel.readyState >= 1) { try { pel.currentTime = pst; } catch { /* noop */ } }
                if (pel.readyState >= 2) drawCover(ctx, pel, cw, ch);
              }
            }
            alpha = p;
          }
        }
        if (el && el.readyState >= 2) {
          alpha *= clipOpacity(clip.o);
          const editingCrop = cropEditRef.current && cropEditRef.current.id === clip.o.id;
          if (isShaped(clip.o)) {
            drawShapedVideo(ctx, el, cw, ch, clip.o, alpha);
          } else {
            ctx.save();
            ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
            ctx.filter = cssFilter(clip.o.filters);
            if (tx !== 0 || sc !== 1) { ctx.translate(cw / 2 + tx, ch / 2); ctx.scale(sc, sc); ctx.translate(-cw / 2, -ch / 2); }
            if (!editingCrop) applyCamera(ctx, cameraTf(clip.o, t), cw, ch, { vw: el.videoWidth, vh: el.videoHeight, mode: fitRef.current.fitMode, crop: clipCrop(clip.o) });
            // While crop handles are shown, draw the full frame so the user sees what they're cutting.
            drawCover(ctx, el, cw, ch, editingCrop ? null : clipCrop(clip.o), alpha < 1 && fitRef.current.letterbox !== "blur");
            if (editingCrop) cropGeomRef.current = { vw: el.videoWidth, vh: el.videoHeight, cw, ch };
            ctx.restore();
            ctx.filter = "none";
          }
        }
      } else {
        activeElRef.current = null;
      }
      // 2) Image overlays (composited above video, below captions)
      resolveActiveImages(stateRef.current.mediaTracks, t).forEach(({ o }) => {
        const img = getImage(absUrl(o.asset_url));
        if (!img || !img.naturalWidth) return;
        const fx = computeImageEffect(o, t);
        const scl = Math.max(0.02, Math.min(1, (o.scale != null ? o.scale : 0.5))) * fx.scale;
        const boxW = cw * scl;
        const shape = o.shape || "rect";
        const fit = o.fit || "contain";
        const cr = o.cornerRadius || 0;
        const needsPrep = shape !== "rect" || fit === "cover" || fit === "fill" || fit === "stretch" || cr > 0;
        let iw, ih;
        if (!needsPrep) { iw = boxW; ih = iw * (img.naturalHeight / img.naturalWidth); }
        else if (shape === "circle" || shape === "square") { iw = boxW; ih = boxW; }
        else if (shape === "oval") { iw = boxW; ih = boxW * 0.7; }
        else { iw = boxW; ih = o.boxH ? ch * o.boxH : boxW * 0.75; }
        const cx = ((o.x != null ? o.x : 0.5) + fx.dx) * cw;
        const cy = ((o.y != null ? o.y : 0.5) + fx.dy) * ch;
        const op = (o.opacity != null ? o.opacity : 1) * fx.alpha;
        const rot = (o.rotation || 0) * Math.PI / 180;
        ctx.save();
        ctx.globalAlpha = Math.max(0, Math.min(1, op));
        ctx.filter = fx.blur > 0.1 ? `${cssFilter(o.filters)} blur(${fx.blur.toFixed(1)}px)`.trim() : cssFilter(o.filters);
        ctx.translate(cx, cy);
        if (rot) ctx.rotate(rot);
        if (needsPrep) {
          ctx.beginPath();
          if (shape === "circle" || shape === "oval") ctx.ellipse(0, 0, iw / 2, ih / 2, 0, 0, Math.PI * 2);
          else { const rad = (shape === "rounded" || cr > 0) ? Math.min(iw, ih) * Math.min(0.5, cr || 0.12) : 0; if (ctx.roundRect) ctx.roundRect(-iw / 2, -ih / 2, iw, ih, rad); else ctx.rect(-iw / 2, -ih / 2, iw, ih); }
          ctx.clip();
          const sw = img.naturalWidth, sh = img.naturalHeight;
          if (fit === "fill" || fit === "stretch") ctx.drawImage(img, -iw / 2, -ih / 2, iw, ih);
          else {
            const r = fit === "cover" ? Math.max(iw / sw, ih / sh) : Math.min(iw / sw, ih / sh);
            const dw = sw * r, dh = sh * r;
            ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
          }
        } else {
          ctx.drawImage(img, -iw / 2, -ih / 2, iw, ih);
        }
        ctx.restore();
        ctx.filter = "none";
      });
      // 2b) Design elements (shapes/lines/arrows) — above images, below captions.
      resolveActiveElements(stateRef.current.mediaTracks, t).forEach(({ o }) => {
        drawElement(ctx, o, cw, ch);
      });
    }

    // 3) Captions / text animations (skip in "video" mode)
    if (pm !== "video") {
      if (hasAnyCaptionObjects(caps)) {
        const hb = [];
        drawCaptionFrame(ctx, t, cw, ch, caps, cfg, segs, { hitboxes: hb, selectedId: selId, noClear: true });
        hitboxesRef.current = hb;
      } else {
        drawFrame(ctx, t, cw, ch, segs, cfg, { noClear: true });
        hitboxesRef.current = [];
      }
    } else {
      hitboxesRef.current = [];
    }
  }, [effectiveActiveClip, getVideo, getImage]);

  const drawRef = useRef(draw);
  useEffect(() => { drawRef.current = draw; }, [draw]);
  const [, setGeomTick] = useState(0);
  useEffect(() => {
    if (!cropEdit) { cropGeomRef.current = null; }
    if (!playingRef.current) drawRef.current(curRef.current);
    setGeomTick((n) => n + 1);
  }, [cropEdit?.id, cropEdit?.crop]); // eslint-disable-line react-hooks/exhaustive-deps

  // Play music / voiceover clips (their own source) mixed with the active video's audio.
  const syncAudio = useCallback((t, isPlaying) => {
    const map = audiosRef.current;
    if (!isPlaying) { map.forEach((a) => { try { a.pause(); } catch { /* noop */ } }); return; }
    const activeUrls = new Set();
    audioClips(stateRef.current.mediaTracks).forEach(({ tr, o }) => {
      const s = o.start || 0, e = o.end ?? (s + 1);
      const url = absUrl(o.asset_url);
      if (!url) return;
      const el = getAudio(url);
      if (!el) return;
      const active = t >= s - 1e-3 && t < e - 1e-3 && !tr.hidden && !o.hidden && !tr.muted && !o.muted;
      if (!active) { try { el.pause(); } catch { /* noop */ } return; }
      activeUrls.add(url);
      let gain = 1;
      const fi = o.fade_in || 0, fo = o.fade_out || 0;
      if (fi > 0 && t < s + fi) gain *= (t - s) / fi;
      if (fo > 0 && t > e - fo) gain *= Math.max(0, (e - t) / fo);
      const desired = (o.source_start || 0) + (t - s);
      el.muted = mutedRef.current;
      el.volume = Math.max(0, Math.min(1, volRef.current * (o.volume != null ? o.volume : 1) * gain));
      el.playbackRate = speedRef.current;
      if (Math.abs(el.currentTime - desired) > 0.3) { try { el.currentTime = desired; } catch { /* noop */ } }
      const p = el.play(); if (p && p.catch) p.catch(() => {});
    });
    map.forEach((a, url) => { if (!activeUrls.has(url)) { try { a.pause(); } catch { /* noop */ } } });
  }, [getAudio]);

  // Point the active source video at the correct source time for timeline time t.
  const syncMedia = useCallback((t, isPlaying) => {
    syncAudio(t, isPlaying);
    // Background video (looping, muted by default) — driven independently of the main clip.
    const bg = backgroundObject(stateRef.current.mediaTracks);
    if (bg && bg.bgType === "video" && (bg.asset_url || bg.assetUrl)) {
      const bel = getVideo(absUrl(bg.asset_url || bg.assetUrl));
      if (bel) {
        bel.muted = bg.muted !== false || mutedRef.current;
        bel.loop = bg.loop !== false;
        if (isPlaying) { const p = bel.play(); if (p && p.catch) p.catch(() => {}); }
        else {
          try { bel.pause(); } catch { /* noop */ }
          const off = bg.source_start || 0;
          const want = bel.duration ? off + ((t) % Math.max(0.1, bel.duration - off)) : off;
          if (Math.abs(bel.currentTime - want) > 0.2) { try { bel.currentTime = want; } catch { /* noop */ } }
        }
      }
    }
    const clip = effectiveActiveClip(t);
    const map = videosRef.current;
    if (!clip) { map.forEach((v) => { try { v.pause(); } catch { /* noop */ } }); activeElRef.current = null; return; }
    const url = clipSourceUrl(clip.o, stateRef.current.videoUrl);
    const el = getVideo(url);
    activeElRef.current = el;
    if (!el) return;
    map.forEach((v) => { if (v !== el) { try { v.pause(); } catch { /* noop */ } } });
    const trackMuted = clip.tr.muted || clip.o.muted;
    el.muted = mutedRef.current || !!trackMuted;
    el.volume = Math.max(0, Math.min(1, volRef.current * (clip.o.volume != null ? clip.o.volume : 1)));
    let desired = sourceTimeFor(clip.o, t);
    if (el.duration) desired = Math.min(desired, Math.max(0, el.duration - 0.05));
    if (isPlaying) {
      el.playbackRate = speedRef.current;
      if (Math.abs(el.currentTime - desired) > 0.3) el.currentTime = desired;
      const p = el.play(); if (p && p.catch) p.catch(() => {});
    } else {
      try { el.pause(); } catch { /* noop */ }
      if (Math.abs(el.currentTime - desired) > 0.03) { try { el.currentTime = desired; el.__primed = true; } catch { /* noop */ } }
      else if (!el.__primed && el.readyState >= 2) { try { el.currentTime = desired + 0.001; el.__primed = true; } catch { /* noop */ } }
    }
  }, [effectiveActiveClip, getVideo, syncAudio]);

  const tick = useCallback(() => {
    if (!playingRef.current) return;
    const now = performance.now();
    let t = clockRef.current.t + ((now - clockRef.current.wall) / 1000) * speedRef.current;
    const d = stateRef.current.duration || 0;
    if (d && t >= d) {
      t = d;
      curRef.current = t; setTime(t);
      syncMedia(t, false); drawRef.current(t); onTime && onTime(t, false);
      doPause();
      return;
    }
    curRef.current = t; setTime(t);
    syncMedia(t, true); drawRef.current(t); onTime && onTime(t, true);
    rafRef.current = requestAnimationFrame(tick);
  }, [onTime, syncMedia]);

  const doPlay = useCallback(() => {
    if (playingRef.current) return;
    const d = stateRef.current.duration || 0;
    if (d && curRef.current >= d - 0.01) curRef.current = 0;
    playingRef.current = true; setPlaying(true);
    clockRef.current = { wall: performance.now(), t: curRef.current };
    onPlayStateChange && onPlayStateChange(true);
    syncMedia(curRef.current, true);
    rafRef.current = requestAnimationFrame(tick);
  }, [onPlayStateChange, syncMedia, tick]);

  const doPause = useCallback(() => {
    playingRef.current = false; setPlaying(false);
    cancelAnimationFrame(rafRef.current);
    videosRef.current.forEach((v) => { try { v.pause(); } catch { /* noop */ } });
    audiosRef.current.forEach((a) => { try { a.pause(); } catch { /* noop */ } });
    onPlayStateChange && onPlayStateChange(false);
  }, [onPlayStateChange]);

  const seekTo = useCallback((t) => {
    t = Math.max(0, Math.min(stateRef.current.duration || t, t));
    curRef.current = t; setTime(t);
    if (playingRef.current) clockRef.current = { wall: performance.now(), t };
    syncMedia(t, playingRef.current);
    drawRef.current(t);
    onTime && onTime(t, playingRef.current);
  }, [onTime, syncMedia]);

  useImperativeHandle(ref, () => ({
    seekTo,
    play: doPlay,
    pause: doPause,
    toggle: () => (playingRef.current ? doPause() : doPlay()),
    getTime: () => curRef.current,
  }));

  // Stop & release any pooled <video>/<audio> whose clip was removed from the timeline, so a
  // deleted clip can never keep playing in the preview (source is truth).
  const pruneMedia = useCallback(() => {
    const mt = stateRef.current.mediaTracks || [];
    const vurls = new Set([stateRef.current.videoUrl]);
    videoClips(mt).forEach(({ o }) => vurls.add(clipSourceUrl(o, stateRef.current.videoUrl)));
    videosRef.current.forEach((el, url) => {
      if (!vurls.has(url)) { try { el.pause(); el.removeAttribute("src"); el.load(); } catch { /* noop */ } if (activeElRef.current === el) activeElRef.current = null; videosRef.current.delete(url); }
    });
    const aurls = new Set();
    audioClips(mt).forEach(({ o }) => aurls.add(absUrl(o.asset_url)));
    audiosRef.current.forEach((el, url) => {
      if (!aurls.has(url)) { try { el.pause(); el.removeAttribute("src"); el.load(); } catch { /* noop */ } audiosRef.current.delete(url); }
    });
  }, []);

  const sizeCanvas = useCallback(() => {
    const cv = canvasRef.current, wrap = wrapRef.current;
    if (!cv || !wrap) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = Math.round(wrap.clientWidth * dpr);
    cv.height = Math.round(wrap.clientHeight * dpr);
    drawRef.current(curRef.current);
  }, []);

  useEffect(() => { sizeCanvas(); window.addEventListener("resize", sizeCanvas); return () => window.removeEventListener("resize", sizeCanvas); }, [sizeCanvas]);
  // Recompute after entering/exiting fullscreen — the aspect-locked stage changes size
  // but keeps the PROJECT ratio (letterboxed), so relative object positions stay exact.
  useEffect(() => {
    const onFs = () => { setTimeout(() => sizeCanvas(), 60); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [sizeCanvas]);
  // Telugu/Devanagari/Latin fonts must be loaded before the canvas can draw their
  // glyphs (canvas silently falls back to tofu otherwise). Force-load them and
  // redraw whenever the fonts become ready.
  useEffect(() => {
    let alive = true;
    const off = onFontsReady(() => { if (alive && !playingRef.current) drawRef.current(curRef.current); });
    ensureScriptFonts();
    return () => { alive = false; off(); };
  }, []);
  // Preload exactly the glyph subsets the current captions/transcript need, then redraw.
  useEffect(() => {
    let alive = true;
    const text = collectCaptionText(captionLayers, segments);
    const fams = new Set([config?.fontFamily, config?.fontFamilyB, ...Object.values(config?.wordStyles || {}).map((w) => w?.fontFamily)]);
    (captionLayers || []).forEach((l) => (l.objects || []).forEach((o) => fams.add(o.style?.fontFamily)));
    ensureTextFonts(text, [...fams].filter(Boolean)).then(() => { if (alive && !playingRef.current) drawRef.current(curRef.current); });
    return () => { alive = false; };
  }, [captionLayers, segments, config?.fontFamily, config?.fontFamilyB, config?.wordStyles]);
  // Whenever the timeline changes, release media for removed clips (deleted clips must not play).
  useEffect(() => { pruneMedia(); }, [mediaTracks, pruneMedia]);
  // Redraw when the timeline / captions / config / mode change (paused edits update instantly).
  useEffect(() => { if (!playingRef.current) { syncMedia(curRef.current, false); drawRef.current(curRef.current); } }, [config, previewMode, segments, captionLayers, mediaTracks, selectedObjId, syncMedia]);
  useEffect(() => () => { cancelAnimationFrame(rafRef.current); videosRef.current.forEach((v) => { try { v.pause(); } catch { /* noop */ } }); audiosRef.current.forEach((a) => { try { a.pause(); } catch { /* noop */ } }); }, []);

  // ---- caption canvas interaction (unchanged) ----
  const canvasPoint = (e) => {
    const cv = canvasRef.current; const rect = cv.getBoundingClientRect();
    return { x: (e.clientX - rect.left) * (cv.width / rect.width), y: (e.clientY - rect.top) * (cv.height / rect.height), cv };
  };
  const onCanvasPointerDown = (e) => {
    if (!interactive) return;
    const { x, y, cv } = canvasPoint(e);
    const pad = Math.max(12, cv.width * 0.012);
    const boxes = hitboxesRef.current || [];
    if (selectedObjId) {
      const sb = boxes.find((b) => b.objId === selectedObjId);
      if (sb) {
        const hr = Math.max(16, cv.width * 0.02);
        const corners = [
          [sb.cx - (sb.w / 2 + pad), sb.cy - (sb.h / 2 + pad)], [sb.cx + (sb.w / 2 + pad), sb.cy - (sb.h / 2 + pad)],
          [sb.cx - (sb.w / 2 + pad), sb.cy + (sb.h / 2 + pad)], [sb.cx + (sb.w / 2 + pad), sb.cy + (sb.h / 2 + pad)],
        ];
        const onHandle = corners.some(([hx, hy]) => Math.hypot(x - hx, y - hy) <= hr);
        if (onHandle) {
          e.preventDefault();
          const lyr = (captionLayers || []).find((l) => l.id === sb.layerId);
          const obj = lyr && (lyr.objects || []).find((o) => o.id === sb.objId);
          const baseScale = (obj && obj.scale) || 1;
          const startDist = Math.max(1, Math.hypot(x - sb.cx, y - sb.cy));
          const move = (ev) => {
            const p = canvasPoint(ev);
            const dist = Math.hypot(p.x - sb.cx, p.y - sb.cy);
            const scale = Math.max(0.2, Math.min(5, baseScale * (dist / startDist)));
            onMoveCaption && onMoveCaption(sb.layerId, sb.objId, { scale: +scale.toFixed(3) });
          };
          const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
          window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
          return;
        }
      }
    }
    let hit = null;
    for (let i = boxes.length - 1; i >= 0; i--) {
      const b = boxes[i];
      if (Math.abs(x - b.cx) <= b.w / 2 + pad && Math.abs(y - b.cy) <= b.h / 2 + pad) { hit = b; break; }
    }
    if (!hit) { onSelectCaption && onSelectCaption(null, null); return; }
    e.preventDefault();
    onSelectCaption && onSelectCaption(hit.layerId, hit.objId);
    dragRef.current = { layerId: hit.layerId, objId: hit.objId, offX: x - hit.cx, offY: y - hit.cy, moved: false };
    const move = (ev) => {
      const p = canvasPoint(ev);
      const posX = Math.max(0, Math.min(1, (p.x - dragRef.current.offX) / p.cv.width));
      const posY = Math.max(0, Math.min(1, (p.y - dragRef.current.offY) / p.cv.height));
      dragRef.current.moved = true;
      onMoveCaption && onMoveCaption(dragRef.current.layerId, dragRef.current.objId, { posX: +posX.toFixed(4), posY: +posY.toFixed(4) });
    };
    const up = () => { dragRef.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const setRate = (r) => { setSpeed(r); speedRef.current = r; const el = activeElRef.current; if (el) el.playbackRate = r; };
  const toggleMute = () => { const m = !muted; setMuted(m); mutedRef.current = m; const el = activeElRef.current; if (el) el.muted = m; };
  const changeVol = (val) => { setVolume(val); volRef.current = val; const m = val === 0; setMuted(m); mutedRef.current = m; const el = activeElRef.current; if (el) { el.volume = val; el.muted = m; } };
  const fullscreen = () => {
    const el = outerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  };

  return (
    <div className="flex h-full flex-col">
      <div ref={outerRef} className="flex flex-1 items-center justify-center overflow-hidden bg-black p-3">
        <div ref={wrapRef} data-testid="video-stage"
          className={`relative overflow-hidden rounded-xl border border-white/10 shadow-2xl ${previewMode === "overlay" ? "checker" : "bg-black"}`}
          style={{ aspectRatio: ratio, maxHeight: "100%", maxWidth: "100%", height: "100%" }}>
          <div ref={hiddenRef} style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }} data-testid="decode-pool" />
          <canvas ref={canvasRef} data-testid="overlay-canvas" onPointerDown={onCanvasPointerDown}
            className={`absolute inset-0 h-full w-full ${interactive ? "cursor-move" : "pointer-events-none"}`} />
          {cropEdit && <CropOverlay clip={cropEdit} geom={cropGeomRef.current || { vw: videoMeta?.width || 16, vh: videoMeta?.height || 9, cw: canvasRef.current?.width || 16, ch: canvasRef.current?.height || 9 }}
            fitMode={fitMode} onChange={onCropChange} onDone={onCropDone} />}
          {trackDraw && <TrackOverlay {...trackDraw} />}
        </div>
      </div>

      {/* Controls */}
      <div className="border-t border-white/10 bg-[#0d0d0d] px-4 py-3" data-testid="video-controls">
        <input type="range" min={0} max={duration || 0} step={0.01} value={time} data-testid="seek-bar"
          onChange={(e) => seekTo(parseFloat(e.target.value))}
          className="mb-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/15 accent-[#00F0FF]"
          style={{ background: `linear-gradient(90deg,#00F0FF ${(time / (duration || 1)) * 100}%, rgba(255,255,255,0.15) ${(time / (duration || 1)) * 100}%)` }} />
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <button data-testid="skip-back" onClick={() => seekTo(Math.max(0, curRef.current - 2))} className="rounded-md p-2 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><Rewind size={16} /></button>
            <button data-testid="play-pause-btn" onClick={() => (playingRef.current ? doPause() : doPlay())} className="flex h-10 w-10 items-center justify-center rounded-full bg-[#00F0FF] text-black transition-transform hover:scale-105">
              {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <button data-testid="skip-fwd" onClick={() => seekTo(Math.min(duration, curRef.current + 2))} className="rounded-md p-2 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><FastForward size={16} /></button>
            <div className="ml-2 font-plex text-sm tabular-nums text-[#A1A1AA]" data-testid="time-display">
              <span className="text-white">{fmtTime(time)}</span> / {fmtTime(duration)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <button onClick={toggleMute} className="rounded-md p-1.5 text-[#A1A1AA] transition-colors hover:text-white">{muted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
              <input type="range" min={0} max={1} step={0.05} value={muted ? 0 : volume} onChange={(e) => changeVol(parseFloat(e.target.value))} className="hidden h-1 w-20 cursor-pointer appearance-none rounded-full bg-white/15 accent-[#00F0FF] sm:block" />
            </div>
            <select data-testid="speed-select" value={speed} onChange={(e) => setRate(parseFloat(e.target.value))}
              className="rounded-md border border-white/10 bg-[#1a1a1a] px-2 py-1.5 text-xs font-semibold text-white outline-none">
              {SPEEDS.map((s) => <option key={s} value={s}>{`${s}×`}</option>)}
            </select>
            <button onClick={fullscreen} className="rounded-md p-2 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><Maximize size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default VideoPlayer;
